import { ArrowUpRight, Hammer, Sofa, Shield, Sun, Frame } from "lucide-react";

const CATEGORIES = [
  {
    title: "Recinzioni & Sicurezza",
    desc: "Recinzioni moderne, cancelli carrabili e pedonali, parapetti certificati per balconi e terrazze.",
    icon: Shield,
    items: ["Cancelli motorizzati", "Parapetti vetro+ferro", "Recinzioni industriali"],
    accent: "from-[#FF5A1F] to-[#EA450C]",
    span: "lg:col-span-7",
    h: "h-[360px]",
  },
  {
    title: "Ringhiere su misura",
    desc: "Design moderno o classico, finiture industriali o verniciate a polvere.",
    icon: Hammer,
    items: ["Ferro battuto", "Acciaio inox", "Verniciatura a polvere"],
    accent: "from-slate-700 to-slate-900",
    span: "lg:col-span-5",
    h: "h-[360px]",
  },
  {
    title: "Arredo Industriale Ferro + Legno",
    desc: "Tavoli con piedi in ferro e piano in legno massello, consolle, panche e sedute industriali realizzate a mano.",
    icon: Sofa,
    items: ["Tavoli ferro+massello", "Consolle", "Panche industriali"],
    accent: "from-slate-700 to-slate-900",
    span: "lg:col-span-5",
    h: "h-[380px]",
  },
  {
    title: "Pareti Attrezzate Industrial",
    desc: "Pareti attrezzate per soggiorno in stile industriale: librerie ferro+legno, mensole sospese, mobili TV su misura.",
    icon: Frame,
    items: ["Librerie", "Mensole sospese", "Mobile TV"],
    accent: "from-[#FF5A1F] to-[#EA450C]",
    span: "lg:col-span-7",
    h: "h-[380px]",
  },
];

const WORKSHOP_IMG =
  "https://static.prod-images.emergentagent.com/jobs/4f3190bd-1b22-40d9-b386-e9731f443b12/images/94167a06f34e57a4e02f8ac29dd7a5931f6d87b969a799f08baad1da5de20f8e.png";

export default function CarpenteriaShowcase() {
  return (
    <section
      id="carpenteria"
      data-testid="carpenteria-showcase"
      className="relative bg-[#050B14] py-28 md:py-40"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        {/* Header */}
        <div className="grid md:grid-cols-12 gap-10 mb-16 items-end">
          <div className="md:col-span-7">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-px bg-[#FF5A1F]" />
              <span className="text-xs tracking-[0.3em] uppercase text-[#FF5A1F] font-bold">
                Carpenteria & Design
              </span>
            </div>
            <h2 className="headline text-white text-4xl md:text-6xl">
              Dal cancello{" "}
              <span className="font-display italic font-medium text-slate-300">
                al tavolo di casa.
              </span>
            </h2>
          </div>
          <div className="md:col-span-5">
            <p className="text-slate-400 text-base md:text-lg leading-relaxed">
              Nella nostra officina interna lavoriamo ferro, acciaio e legno
              massello su misura. Una linea di prodotti che va dalla{" "}
              <span className="text-white">sicurezza</span> per i tuoi spazi
              esterni all'<span className="text-white">arredo industriale</span>{" "}
              che ridefinisce gli ambienti di casa.
            </p>
          </div>
        </div>

        {/* Officina banner */}
        <div className="relative h-[220px] md:h-[280px] mb-5 overflow-hidden border border-[#1E293B]">
          <img
            src={WORKSHOP_IMG}
            alt="Officina Dea srl"
            className="absolute inset-0 w-full h-full object-cover grayscale-[15%]"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#050B14] via-[#050B14]/70 to-transparent" />
          <div className="relative h-full flex items-center px-8 md:px-14 max-w-2xl">
            <div>
              <div className="text-xs tracking-[0.3em] uppercase text-[#FF5A1F] font-bold mb-3">
                Officina interna · Garlasco
              </div>
              <h3 className="text-2xl md:text-4xl font-bold text-white tracking-tight leading-tight">
                Tutto lavorato a mano nel nostro capannone.
              </h3>
            </div>
          </div>
        </div>

        {/* Bento grid - typographic cards */}
        <div className="grid lg:grid-cols-12 gap-4 md:gap-5">
          {CATEGORIES.map((c, i) => {
            const Icon = c.icon;
            const isAccent = c.accent.includes("FF5A1F");
            return (
              <article
                key={i}
                data-testid={`carp-card-${i}`}
                className={`relative ${c.span} ${c.h} overflow-hidden border ${
                  isAccent
                    ? "border-[#FF5A1F]/40"
                    : "border-[#1E293B]"
                } group transition-transform duration-500 hover:-translate-y-1`}
                style={{
                  backgroundImage: isAccent
                    ? "radial-gradient(circle at top right, rgba(255,90,31,0.18), transparent 60%), linear-gradient(135deg, #0A1120 0%, #050B14 100%)"
                    : "linear-gradient(135deg, #111C30 0%, #0A1120 60%, #050B14 100%)",
                }}
              >
                {/* Decorative pattern */}
                <div className="absolute inset-0 opacity-10 pointer-events-none">
                  <div
                    className="absolute -right-10 -top-10 w-64 h-64 border-2"
                    style={{
                      borderColor: isAccent ? "#FF5A1F" : "#1E293B",
                      transform: "rotate(15deg)",
                    }}
                  />
                  <div
                    className="absolute -right-20 -top-20 w-80 h-80 border"
                    style={{
                      borderColor: isAccent ? "#FF5A1F" : "#1E293B",
                      transform: "rotate(30deg)",
                    }}
                  />
                </div>

                <div className="relative h-full p-7 md:p-9 flex flex-col justify-between">
                  <div className="flex items-center justify-between">
                    <div
                      className={`w-12 h-12 flex items-center justify-center ${
                        isAccent ? "bg-[#FF5A1F]" : "bg-[#FF5A1F]/15 border border-[#FF5A1F]/40"
                      }`}
                    >
                      <Icon
                        size={22}
                        className={isAccent ? "text-white" : "text-[#FF5A1F]"}
                        strokeWidth={2}
                      />
                    </div>
                    <span className="font-mono text-xs tracking-[0.25em] text-slate-500">
                      / 0{i + 1}
                    </span>
                  </div>

                  <div>
                    <h3 className="text-2xl md:text-3xl font-bold text-white tracking-tight leading-tight">
                      {c.title}
                    </h3>
                    <p className="mt-4 text-slate-300 text-sm md:text-base leading-relaxed max-w-md">
                      {c.desc}
                    </p>
                    <div className="mt-6 flex flex-wrap gap-2">
                      {c.items.map((t) => (
                        <span
                          key={t}
                          className="text-[11px] tracking-wider uppercase border border-slate-600 bg-[#050B14]/40 text-slate-300 px-2.5 py-1"
                        >
                          {t}
                        </span>
                      ))}
                    </div>
                    <a
                      href="/carpenteria"
                      className="mt-7 inline-flex items-center gap-2 text-sm text-white font-semibold link-underline"
                    >
                      Vedi il portfolio <ArrowUpRight size={16} />
                    </a>
                  </div>
                </div>
              </article>
            );
          })}
        </div>

        {/* Portfolio CTA - link a pagina dedicata */}
        <div className="mt-8 md:mt-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-5 bg-[#0A1120] border border-[#1E293B] p-6 md:p-8">
          <div>
            <div className="text-xs tracking-[0.3em] uppercase text-[#FF5A1F] font-bold mb-2">
              Portfolio completo
            </div>
            <div className="text-white font-bold text-xl md:text-2xl tracking-tight">
              Guarda tutti i nostri lavori di carpenteria
            </div>
            <div className="text-slate-400 text-sm mt-2">
              Recinzioni, cancelli taglio laser, ringhiere e arredo industriale
              realizzati nella nostra officina.
            </div>
          </div>
          <a
            href="/carpenteria"
            data-testid="portfolio-cta-main"
            className="inline-flex items-center gap-3 bg-[#FF5A1F] hover:bg-[#EA450C] text-white px-6 py-4 text-sm font-semibold whitespace-nowrap transition-colors"
          >
            Sfoglia il portfolio
            <ArrowUpRight size={18} />
          </a>
        </div>

        {/* Fotovoltaico banner */}
        <div className="mt-5 grid md:grid-cols-12 gap-5">
          <div className="md:col-span-12 relative bg-[#0A1120] border border-[#1E293B] overflow-hidden">
            <div className="grid md:grid-cols-12 items-stretch">
              <div
                className="md:col-span-5 relative min-h-[260px]"
                style={{
                  background:
                    "linear-gradient(135deg, #FF5A1F 0%, #EA450C 50%, #C73A0A 100%)",
                }}
              >
                <div className="absolute inset-0 opacity-30 pointer-events-none">
                  {[...Array(12)].map((_, i) => (
                    <div
                      key={i}
                      className="absolute bg-white/20"
                      style={{
                        width: "60px",
                        height: "40px",
                        top: `${15 + (i % 4) * 22}%`,
                        left: `${15 + Math.floor(i / 4) * 28}%`,
                        transform: "skewY(-12deg)",
                      }}
                    />
                  ))}
                </div>
                <div className="relative h-full flex items-center justify-center p-8">
                  <Sun size={120} className="text-white/95" strokeWidth={1.2} />
                </div>
              </div>
              <div className="md:col-span-7 p-8 md:p-10 flex flex-col justify-center">
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-xs tracking-[0.3em] uppercase text-[#FF5A1F] font-bold">
                    Nuovo Servizio
                  </span>
                </div>
                <h3 className="text-2xl md:text-3xl font-bold text-white tracking-tight">
                  Impianti Fotovoltaici{" "}
                  <span className="font-display italic font-medium text-slate-300">
                    chiavi in mano.
                  </span>
                </h3>
                <p className="mt-4 text-slate-400 leading-relaxed">
                  Progettazione affidata a un nostro partner specializzato,{" "}
                  <span className="text-white">posa e installazione</span>{" "}
                  direttamente con la nostra squadra. Soluzioni residenziali e
                  condominiali con supporto pratiche e incentivi.
                </p>
                <a
                  href="#preventivo"
                  data-testid="fotovoltaico-cta"
                  className="mt-6 inline-flex items-center gap-2 text-[#FF5A1F] font-semibold link-underline self-start"
                >
                  Richiedi preventivo fotovoltaico <ArrowUpRight size={16} />
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
